import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { RefreshCw, TrendingUp, TrendingDown, AlertTriangle, Bell, BellOff } from 'lucide-react';
import axios from 'axios';

const API_V2 = process.env.REACT_APP_BACKEND_URL + '/api/v2';

const ArbitrageScanner = ({ onAlert }) => {
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [threshold, setThreshold] = useState(0.5);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [opportunities, setOpportunities] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const scanArbitrage = useCallback(async () => {
    setScanning(true);
    try {
      const response = await axios.get(`${API_V2}/arbitrage/scan?threshold=${threshold}`);
      if (response.data.success) {
        setOpportunities(response.data.all_opportunities);
        setAlerts(response.data.alerts);
        setLastScan(new Date().toLocaleTimeString());
        
        // Trigger alert callback if there are new alerts
        if (response.data.alerts.length > 0 && alertsEnabled && onAlert) {
          onAlert(response.data.alerts);
        }
      }
    } catch (error) {
      console.error('Failed to scan arbitrage:', error);
    } finally {
      setScanning(false);
    }
  }, [threshold, alertsEnabled, onAlert]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(scanArbitrage, 15000);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, scanArbitrage]);

  // Initial scan on mount
  useEffect(() => {
    scanArbitrage();
  }, []);

  const getSpreadColor = (spread) => {
    if (spread >= 1) return 'text-green-400';
    if (spread >= 0.5) return 'text-yellow-400';
    if (spread >= 0.25) return 'text-orange-400';
    return 'text-gray-400';
  };

  const formatPrice = (price, pair) => {
    if (!price) return '-';
    // Handle very small prices (like SHIB)
    if (price < 0.01) {
      return price.toFixed(8);
    }
    if (price < 1) {
      return price.toFixed(6);
    }
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getExchangeColor = (exchange) => {
    const colors = {
      coinbase: 'text-blue-400',
      binance: 'text-yellow-400',
      kraken: 'text-purple-400',
      cryptocom: 'text-cyan-400',
    };
    return colors[exchange] || 'text-gray-400';
  };

  return (
    <Card className="matrix-card rounded-none" data-testid="arbitrage-scanner">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[#00FF41] font-mono text-sm flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            ARBITRAGE SCANNER
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`p-1 ${autoRefresh ? 'text-[#00FF41]' : 'text-gray-500'}`}
              title={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
              data-testid="arbitrage-auto-refresh"
            >
              {autoRefresh ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={scanArbitrage}
              disabled={scanning}
              className="p-1 text-[#00FF41] hover:bg-[#003B00]"
              data-testid="arbitrage-refresh"
            >
              <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Threshold Configuration */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[#008F11] text-xs">Alert Threshold</span>
            <span className="text-[#00FF41] font-mono text-xs">{threshold.toFixed(1)}%</span>
          </div>
          <Slider
            value={[threshold]}
            onValueChange={(v) => setThreshold(v[0])}
            min={0.1}
            max={5}
            step={0.1}
            className="w-full"
            data-testid="arbitrage-threshold-slider"
          />
          <div className="flex justify-between text-[10px] text-gray-500">
            <span>0.1%</span>
            <span>5%</span>
          </div>
        </div>

        {/* Alerts Section */}
        {alerts.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-yellow-400 text-xs">
              <AlertTriangle className="w-3 h-3" />
              <span>{alerts.length} ARBITRAGE ALERT{alerts.length > 1 ? 'S' : ''}</span>
            </div>
            {alerts.slice(0, 3).map((alert, idx) => (
              <div
                key={idx}
                className="bg-yellow-900/20 border border-yellow-600/30 p-2 rounded"
                data-testid={`arbitrage-alert-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-yellow-400 font-mono text-sm">{alert.pair}</span>
                  <span className={`font-bold ${getSpreadColor(alert.spread_percent)}`}>
                    +{alert.spread_percent.toFixed(2)}%
                  </span>
                </div>
                <div className="text-[10px] text-gray-400 mt-1">
                  Buy <span className={getExchangeColor(alert.buy_from)}>{alert.buy_from?.toUpperCase()}</span> @ ${formatPrice(alert.buy_price, alert.pair)}
                  {' → '}
                  Sell <span className={getExchangeColor(alert.sell_to)}>{alert.sell_to?.toUpperCase()}</span> @ ${formatPrice(alert.sell_price, alert.pair)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* All Opportunities Table */}
        <div className="space-y-1">
          <div className="text-[#008F11] text-xs mb-2">ALL PAIRS</div>
          <div className="max-h-[200px] overflow-y-auto space-y-1">
            {opportunities.map((opp, idx) => (
              <div
                key={opp.pair}
                className="flex items-center justify-between py-1 px-2 bg-[#001a00] hover:bg-[#002200] transition-colors"
                data-testid={`arbitrage-pair-${opp.pair}`}
              >
                <span className="text-[#00FF41] font-mono text-xs w-20">{opp.pair.split('-')[0]}</span>
                
                {/* Exchange Prices */}
                <div className="flex gap-2 text-[10px]">
                  {['coinbase', 'binance', 'kraken', 'cryptocom'].map(ex => (
                    <div key={ex} className="w-16 text-right">
                      <span className={opp.prices[ex] ? getExchangeColor(ex) : 'text-gray-600'}>
                        {opp.prices[ex] ? formatPrice(opp.prices[ex], opp.pair) : '-'}
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Spread */}
                <div className={`font-mono text-xs w-16 text-right ${getSpreadColor(opp.spread_percent)}`}>
                  {opp.spread_percent > 0 ? `+${opp.spread_percent.toFixed(2)}%` : '0.00%'}
                </div>
              </div>
            ))}
          </div>
          
          {/* Exchange Legend */}
          <div className="flex justify-center gap-3 pt-2 text-[9px]">
            <span className="text-blue-400">CB</span>
            <span className="text-yellow-400">BIN</span>
            <span className="text-purple-400">KRK</span>
            <span className="text-cyan-400">CDC</span>
          </div>
        </div>

        {/* Last Scan Info */}
        {lastScan && (
          <div className="text-center text-[10px] text-gray-500">
            Last scan: {lastScan} {autoRefresh && '(auto-refresh ON)'}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ArbitrageScanner;
